import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Select, Table, Tag, Typography } from "antd";
import { HistoryOutlined, InboxOutlined, ShoppingCartOutlined, ShopOutlined } from "@ant-design/icons";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { loadProduct } from "../../redux/actions/product/getAllProductAction";
import "./ProductHistory.css";

const { Title, Text } = Typography;

const formatCurrency = (n) =>
	`₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ProductHistory = () => {
	const dispatch = useDispatch();
	const productList = useSelector((state) => state.products?.list) || [];
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [history, setHistory] = useState({ purchaseHistory: [], saleHistory: [], openingStock: [] });

	const isLogged = Boolean(localStorage.getItem("isLogged"));

	useEffect(() => {
		dispatch(loadProduct({ status: true, page: 1, limit: 5000, viewMode: "stock" }));
	}, [dispatch]);

	const fetchHistory = useCallback(async (productId) => {
		if (!productId) {
			setHistory({ purchaseHistory: [], saleHistory: [], openingStock: [] });
			return;
		}
		setLoading(true);
		try {
			const { data } = await axios.get(`product/history?product_id=${productId}`);
			setHistory({
				purchaseHistory: data.purchaseHistory || [],
				saleHistory: data.saleHistory || [],
				openingStock: data.openingStock || [],
			});
		} catch (err) {
			setHistory({ purchaseHistory: [], saleHistory: [], openingStock: [] });
			console.error(err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (selectedProductId) fetchHistory(selectedProductId);
		else setHistory({ purchaseHistory: [], saleHistory: [], openingStock: [] });
	}, [selectedProductId, fetchHistory]);

	const mergedRows = useMemo(() => {
		const purchase = (history.purchaseHistory || []).map((r, i) => ({
			...r,
			key: `p-${r.date}-${r.invoiceId}-${i}`,
			dateSort: new Date(r.date),
		}));
		const sale = (history.saleHistory || []).map((r, i) => ({
			...r,
			key: `s-${r.date}-${r.invoiceId}-${i}`,
			dateSort: new Date(r.date),
		}));
		const opening = (history.openingStock || []).map((r, i) => ({
			...r,
			key: `o-${r.date}-${i}`,
			dateSort: new Date(r.date),
		}));
		return [...purchase, ...sale, ...opening].sort((a, b) => b.dateSort - a.dateSort);
	}, [history.purchaseHistory, history.saleHistory, history.openingStock]);

	const quantitySummary = useMemo(() => {
		const opening = (history.openingStock || []).reduce((s, r) => s + (Number(r.quantity) || 0), 0);
		const purchased = (history.purchaseHistory || []).reduce((s, r) => s + (Number(r.quantity) || 0), 0);
		const sold = (history.saleHistory || []).reduce((s, r) => s + (Number(r.quantity) || 0), 0);
		return { opening, purchased, sold, net: opening + purchased - sold };
	}, [history.openingStock, history.purchaseHistory, history.saleHistory]);

	const columns = [
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 110,
			render: (type) => {
				if (type === "Purchase") return <Tag icon={<ShoppingCartOutlined />} color="blue">Purchase</Tag>;
				if (type === "Opening Stock") return <Tag icon={<InboxOutlined />} color="orange">Opening Stock</Tag>;
				return <Tag icon={<ShopOutlined />} color="green">Sale</Tag>;
			},
		},
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			width: 120,
			render: (date) => moment(date).format("DD MMM YYYY"),
		},
		{
			title: "Party",
			dataIndex: "party",
			key: "party",
			ellipsis: true,
			render: (party) => <Text strong>{party}</Text>,
		},
		{
			title: "Qty",
			dataIndex: "quantity",
			key: "quantity",
			width: 80,
			align: "right",
		},
		{
			title: "Unit Price",
			dataIndex: "unitPrice",
			key: "unitPrice",
			width: 110,
			align: "right",
			render: (v, row) => row.type === "Opening Stock" ? "—" : formatCurrency(v),
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			width: 120,
			align: "right",
			render: (v, row) => row.type === "Opening Stock" ? "—" : formatCurrency(v),
		},
		{
			title: "Discount",
			dataIndex: "discount",
			key: "discount",
			width: 100,
			align: "right",
			render: (v, row) => row.type === "Opening Stock" ? "—" : formatCurrency(v),
		},
		{
			title: "Invoice",
			key: "invoiceRef",
			width: 140,
			render: (_, row) => {
				if (row.type === "Opening Stock" || !row.invoiceId || row.invoiceRef === "—") return row.invoiceRef ?? "—";
				return row.type === "Sale" ? (
					<Link to={`/sale/${row.invoiceId}`}>{row.invoiceRef}</Link>
				) : (
					<Link to={`/purchase/${row.invoiceId}`}>{row.invoiceRef}</Link>
				);
			},
		},
	];

	const productOptions = useMemo(
		() =>
			(Array.isArray(productList) ? productList : []).map((p) => ({
				label: [p.name, p.isbn].filter(Boolean).join(" • ") || `Product #${p.id}`,
				value: p.id,
			})),
		[productList]
	);

	if (!isLogged) {
		return <Navigate to="/auth/login" replace />;
	}

	return (
		<div className="product-history-page">
			<Card className="product-history-card" bordered={false}>
				<div className="product-history-header">
					<HistoryOutlined className="product-history-header-icon" />
					<Title level={4} className="product-history-title">
						Product History
					</Title>
					<Text type="secondary">When, from where and at what price a product was sold or purchased</Text>
				</div>
				<div className="product-history-select-wrap">
					<label className="product-history-label">Select Product</label>
					<Select
						placeholder="Search and select a product"
						allowClear
						showSearch
						optionFilterProp="label"
						value={selectedProductId || undefined}
						onChange={setSelectedProductId}
						options={productOptions}
						style={{ width: "100%", maxWidth: 400 }}
						size="large"
						notFoundContent="No products found"
					/>
				</div>
				{selectedProductId && (
					<div className="product-history-table-wrap">
						{!loading && mergedRows.length > 0 && (
							<div style={{ marginBottom: 12, padding: "8px 12px", background: "#fafafa", borderRadius: 6, fontSize: 13 }}>
								<strong>Closing stock:</strong> {quantitySummary.net}
							</div>
						)}
						<Table
							columns={columns}
							dataSource={mergedRows}
							loading={loading}
							pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total ${t} records` }}
							scroll={{ x: 800 }}
							size="middle"
						/>
					</div>
				)}
				{selectedProductId && !loading && mergedRows.length === 0 && (
					<div className="product-history-empty">No purchase or sale history found for this product.</div>
				)}
			</Card>
		</div>
	);
};

export default ProductHistory;
